import { useState } from 'react'
import { useLocation } from 'react-router'
import { toast } from 'sonner'

import { DocumentMetadataDrawer } from '@/components/documents/DocumentMetadataDrawer'
import { SkeletonRows } from '@/components/ui/skeleton'
import { apiErrorDetail } from '@/lib/apiError'
import type { NamedDocument } from '@/models/document'
import {
  type ComplianceSlot,
  type FileKind,
  uploadTarget,
  useComplianceFile,
  useUploadReceipt,
  useUploadToSlot,
} from '@/models/vault'
import { AuditPackageButton } from './AuditPackageButton'
import { BackLink } from './BackLink'
import { RecordInfoButton } from './RecordInfoButton'
import { type PickRequest, type UploadMode } from './SlotRow'
import { SlotStatusPill } from './SlotStatusPill'
import { SlotTable } from './SlotTable'
import { UploadReceipt } from './UploadReceipt'
import { useVault } from './VaultContext'

/** One thing's whole file — the carrier's, a driver's, a truck's or a trailer's. */
export function ComplianceFileView({
  kind,
  entityId,
  back,
}: {
  kind: FileKind
  /** Which driver or vehicle; the carrier has none. */
  entityId?: string
  back: { to: string; label: string }
}) {
  const { orgId, href } = useVault()
  const { file, isLoading } = useComplianceFile(orgId, kind, entityId)
  // Opened from the timeline, the way back is the timeline — not the folder the route assumes.
  const location = useLocation()
  const fromTimeline = (location.state as { from?: string } | null)?.from === 'timeline'
  const backTarget = fromTimeline ? { to: href('timeline'), label: 'Expiry timeline' } : back
  const uploadMutation = useUploadToSlot(orgId, uploadTarget(kind, entityId))

  // The target outlives the close so the drawer has content to animate out with.
  const [detailsTarget, setDetailsTarget] = useState<{
    document: NamedDocument
    attachmentId: string
  } | null>(null)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [uploadingKey, setUploadingKey] = useState<string | null>(null)
  const [pickRequest, setPickRequest] = useState<PickRequest | null>(null)
  // The last upload, so the file can say what it received and what is still open.
  const [received, setReceived] = useState<{ documentId: string; slot: ComplianceSlot } | null>(null)
  const { receipt } = useUploadReceipt(orgId, received?.documentId ?? null)

  const openDetails = (document: NamedDocument, attachmentId: string) => {
    setDetailsTarget({ document, attachmentId })
    setIsDetailsOpen(true)
  }

  /** A picked or dropped file goes straight up; the row shows the progress. */
  const upload = (slot: ComplianceSlot, picked: File, mode: UploadMode) => {
    setUploadingKey(slot.key)
    uploadMutation.mutate(
      { file: picked, documentType: slot.uploadDocumentType, mode },
      {
        onSuccess: (response) => {
          toast.success(`${slot.label} uploaded.`)
          setReceived({ documentId: String((response.data as { id: string }).id), slot })
        },
        onError: (error) => toast.error(apiErrorDetail(error, 'Could not upload that file.')),
        onSettled: () => setUploadingKey(null),
      }
    )
  }

  if (isLoading || !file) return <SkeletonRows />

  // A file with one section needs no heading between its name and its slots.
  const showSectionHeadings = file.sections.length > 1

  return (
    <div className="flex flex-col gap-5">
      <BackLink to={backTarget.to} label={backTarget.label} />

      <div className="flex flex-wrap items-center gap-1">
        <h2 className="text-xl font-semibold">{file.name}</h2>
        {kind !== 'carrier' && entityId && (
          <RecordInfoButton kind={kind} entityId={entityId} name={file.name} />
        )}
        <SlotStatusPill status={file.status} rollup />
        <div className="ml-auto">
          <AuditPackageButton kind={kind} entityId={entityId} variant="outline" />
        </div>
      </div>

      {received && (
        <UploadReceipt
          file={file}
          slot={received.slot}
          receipt={receipt?.documentId === received.documentId ? receipt : undefined}
          onOpenUpload={(slot) => setPickRequest({ key: slot.key, nonce: Date.now() })}
          onDismiss={() => setReceived(null)}
        />
      )}

      {file.sections.map((section) => (
        <div key={section.key} className="flex flex-col gap-2">
          {showSectionHeadings && (
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold">{section.label}</h3>
              <SlotStatusPill status={section.status} rollup />
            </div>
          )}
          <SlotTable
            slots={section.slots}
            openDetails={openDetails}
            upload={upload}
            uploadingKey={uploadingKey}
            pickRequest={pickRequest}
          />
        </div>
      ))}

      <DocumentMetadataDrawer
        orgId={orgId}
        document={detailsTarget?.document ?? null}
        attachmentId={detailsTarget?.attachmentId}
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
      />
    </div>
  )
}
